import { Box, Button, Typography } from '@mui/material';
import { Loading, Modal, ModalError } from '@vabembarqueja/embarque-ja-shared-components';
import { useEffect, useState } from 'react';

import { ActionButton } from 'src/app/components/ActionButton';
import FileUpload from 'src/app/components/FileUpload';
import { UsuarioService } from 'src/services';

interface ModalAnexoMinutaProps {
  refreshUserInfo: () => Promise<void>;
}

export const ModalAnexoMinuta = ({ refreshUserInfo }: ModalAnexoMinutaProps) => {
  const [erroAnexoMinuta, setErroAnexoMinuta] = useState('');
  const [minutaContratual, setMinutaContratual] = useState<File | null>(null);
  const [orgaoPublico, setOrgaoPublico] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    UsuarioService.buscarConta()
      .then((contaUsuario) => {
        if ('orgaoPublico' in contaUsuario) {
          setOrgaoPublico(contaUsuario.orgaoPublico);
        }
      })
      .catch(() => setOrgaoPublico(false));
  }, []);

  // Função genérica que realmente dispara a migração
  const enviarMigracao = async (file?: File) => {
    try {
      setLoading(true);

      await UsuarioService.solicitarMigracao(file);
      await refreshUserInfo();

      // Se estiver no modal (caso órgão público), fecha
      setOpen(false);
      setMinutaContratual(null);
    } catch (err) {
      console.error('Erro ao solicitar migração:', err);
      setErroAnexoMinuta(
        'Não foi possível enviar sua solicitação no momento. Tente novamente em instantes.'
      );
    } finally {
      setLoading(false);
    }
  };

  const solicitarAnexoMinuta = async () => {
    // Se NÃO é órgão público, já dispara direto sem abrir modal
    if (!orgaoPublico) {
      await enviarMigracao();
      return;
    }

    setOpen(true);
  };

  const solicitarMigracaoPlano = async () => {
    if (!minutaContratual) {
      setErroAnexoMinuta('Arquivo inválido: Envie um arquivo (Docx) de até 5Mb.');
      return;
    }

    await enviarMigracao(minutaContratual);
  };

  return (
    <>
      <Box alignSelf="end">
        <Button variant="contained" onClick={solicitarAnexoMinuta} disabled={loading}>
          <Typography variant="body1" textTransform="none" lineHeight={1}>
            Solicitar pós-pago
          </Typography>
        </Button>
      </Box>

      <Modal
        title="Anexar minuta contratual"
        open={open}
        onClose={() => !loading && setOpen(false)}
        actions={
          <>
            <ActionButton
              variant="outlined"
              color="primary"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancelar
            </ActionButton>
            <ActionButton
              variant="contained"
              color="primary"
              onClick={solicitarMigracaoPlano}
              disabled={loading || !minutaContratual}
            >
              Anexar
            </ActionButton>
          </>
        }
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: { xs: '100%', sm: 480 },
            minHeight: { xs: 160, sm: 190 },
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 1
              }}
            >
              <Loading show bgColor="transparent" />
            </Box>
          )}

          <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            Para que possamos dar continuidade à solicitação, solicitamos a gentileza de anexar a
            minuta contratual.
          </Typography>

          <Box mt={2}>
            <FileUpload
              maxSize={5 * 1024 * 1024}
              accept={{
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
              }}
              onChange={([minuta]) => setMinutaContratual(minuta)}
              inputText="Envie um arquivo (Docx) de até 5Mb."
            />
          </Box>
        </Box>
      </Modal>

      <ModalError
        open={Boolean(erroAnexoMinuta)}
        onClose={() => setErroAnexoMinuta('')}
        setOpen={() => setErroAnexoMinuta('')}
        message={erroAnexoMinuta}
      />
    </>
  );
};
